<template>
	<GlassCard class="interactive-story p-0">
		<!-- Header -->
		<div class="story-header px-4 py-3">
			<div class="d-flex align-items-center gap-3">
				<div class="icon-pill">
					<i class="bi bi-stars"></i>
				</div>
				<div class="flex-grow-1">
					<h2 class="title m-0">Interactive Story</h2>
					<p class="subtitle m-0">
						Craft a custom adventure. Your food choices shape the plot.<span
							v-if="isBasic"
							class="subnote">
							— Plus gets stories with images.</span
						>
					</p>
				</div>
				<div class="badge beta">{{ storyBadgeText }}</div>
			</div>
		</div>

		<!-- Loader Overlay -->
		<div v-if="isLoading" class="loader-overlay">
			<div class="loader-card text-center p-4">
				<div class="loading-icon mb-2">
					<i class="bi bi-magic"></i>
				</div>
				<div class="fw-semibold">Weaving your tale...</div>
				<div class="dots mt-1" aria-hidden="true">
					<span></span><span></span><span></span>
				</div>
			</div>
		</div>

		<!-- Chat Log -->
		<div class="chat-log" ref="chatLogRef" v-show="storyStarted">
			<div
				v-for="(entry, index) in storyHistory"
				:key="entry.entryId"
				class="message-group">
				<!-- AI Prompt -->
				<div
					class="message ai"
					:data-latest-prompt="
						index === storyHistory.length - 1 && entry.userChoice === null
							? 'true'
							: null
					">
					<div class="avatar" aria-hidden="true">AI</div>
					<div v-if="entry.imageUrl" class="story-image">
						<img :src="imageSrc(entry.imageUrl)" alt="Story illustration" />
					</div>
					<div class="bubble">
						{{ entry.prompt }}
					</div>
				</div>

				<!-- Choices -->
				<div class="choices" v-if="entry.choices && entry.choices.length">
					<div
						v-for="choice in entry.choices"
						:key="choice.number"
						class="choice">
						<div class="num">{{ choice.number }}</div>
						<div class="desc">{{ choice.description }}</div>
					</div>
				</div>

				<!-- Awaiting food input indicator for the latest open step -->
				<div
					v-if="
						index === storyHistory.length - 1 &&
						entry.userChoice === null &&
						!isStoryEnded
					"
					class="awaiting">
					<div class="awaiting-icon" aria-hidden="true">
						<i class="bi bi-hourglass-split"></i>
					</div>
					<div class="awaiting-text">
						<span class="label">Awaiting food input to choose</span>
						<span class="sub">Log a meal or drink to unlock your path.</span>
					</div>
					<div class="dots" aria-hidden="true">
						<span></span><span></span><span></span>
					</div>
					<button
						class="btn btn-gradient awaiting-btn"
						type="button"
						@click="goToAdd">
						<i class="bi bi-plus-circle me-1"></i> Add Food/Drink
					</button>
				</div>

				<!-- User Selection (if present) -->
				<div
					class="message you"
					v-if="entry.userChoice !== null"
					:data-latest-choice="isLatestChoice(index) ? 'true' : null">
					<div class="avatar" aria-hidden="true">You</div>
					<div class="bubble">
						<i class="bi bi-check2-circle me-1"></i>
						You chose:
						{{
							entry.choices.find(
								(choice) => +choice.number === +entry.userChoice
							)?.description
						}}
					</div>
				</div>
			</div>
		</div>

		<!-- End state -->
		<div v-if="isStoryEnded" class="system-bubble">
			<i class="bi bi-flag me-2"></i> Story Ended
		</div>

		<!-- Actions when story has started -->
		<div v-if="storyStarted" class="footer-actions px-4 pb-4">
			<button class="btn btn-gradient" @click="startNewStory">
				<i class="bi bi-arrow-repeat me-1"></i> Start New Story
			</button>
		</div>

		<!-- Resume when Start New pressed -->
		<div v-if="showResume && !storyStarted" class="footer-actions px-4 pb-4">
			<button
				class="btn btn-outline-light resume-btn"
				type="button"
				@click="resumeStory">
				<i class="bi bi-arrow-return-left me-1"></i> Back to Current Story
			</button>
		</div>

		<!-- Start Screen -->
		<div v-if="!storyStarted" class="start-screen px-4 pb-4">
			<div class="hero mb-3">
				<div class="glow"></div>
				<div class="emoji" aria-hidden="true">&#128214;&#10024;</div>
				<div class="lead">Describe the kind of story you want.</div>
			</div>
			<div class="input-wrap">
				<div class="input-field">
					<i class="bi bi-pencil-square input-icon"></i>
					<input
						v-model="storyPrompt"
						placeholder="e.g., A cozy mystery about a baker whose pastries predict the future"
						class="form-control fancy-input" />
				</div>
				<button class="btn btn-gradient start-btn" @click="startStory">
					<i class="bi bi-play-fill me-1"></i> Start Story
				</button>
			</div>
			<div class="suggestions mt-3">
				<div class="hint">Try one:</div>
				<button
					v-for="s in suggestions"
					:key="s"
					class="chip"
					type="button"
					@click="useSuggestion(s)">
					<i class="bi bi-lightning-charge me-1"></i>{{ s }}
				</button>
			</div>
		</div>
	</GlassCard>
</template>

<script setup>
	import { ref, onMounted, nextTick, computed } from "vue"
	import getURL from "@/utils/url"
	import { useUserStore } from "@/stores/user"
	import { useRouter } from "vue-router"
	// import { useTokenStore } from "@/stores/token"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import GlassCard from "./GlassCard.vue"
	import Swal from "sweetalert2"

	const storyPrompt = ref("")
	const storyStarted = ref(false)
	const isStoryEnded = ref(false)
	const storyHistory = ref([])
	// const currentOptions = ref([])
	const chatLogRef = ref(null)
	const isLoading = ref(false)
	const showResume = ref(false)
	const router = useRouter()
	const imageSrc = (file) => getURL("image", "story/" + file)
	const userStore = useUserStore()
	const storyBadgeText = computed(() =>
		(userStore?.user?.subscription_type ?? "basic") === "basic"
			? "TEXT ONLY"
			: "WITH IMAGES"
	)
	const isBasic = computed(
		() => (userStore?.user?.subscription_type ?? "basic") === "basic"
	)
	// const latestChoice = ref(null)
	// const latestPrompt = ref(null)

	const suggestions = [
		"Epic fantasy with a culinary twist",
		"Cyberpunk heist powered by street food",
		"Wholesome road trip through farm-to-table towns",
		"Space opera where meals fuel starships",
	]

	function useSuggestion(text) {
		storyPrompt.value = text
	}

	// Track the last entry that contains a user choice
	const lastChoiceIndex = computed(() => {
		for (let i = storyHistory.value.length - 1; i >= 0; i--) {
			if (storyHistory.value[i]?.userChoice !== null) return i
		}
		return -1
	})

	const isLatestChoice = (idx) => idx === lastChoiceIndex.value

	function goToAdd() {
		router.push("/add")
	}

	// const tokenStore = useTokenStore()

	// const { hasToken } = tokenStore

	const fetchLatestStory = async () => {
		try {
			isLoading.value = true

			const response = await fetchWithAuth("/api/story", {
				method: "GET",
				// headers: {
				// 	Authorization: `Bearer ${tokenStore.token}`,
				// },
			})

			const data = await response.json()

			if (data.current_story) {
				const { current_story } = data
				storyStarted.value = true
				isStoryEnded.value = current_story.isStoryEnded
				storyHistory.value = current_story.history.map((entry) => ({
					entryId: entry.entryId,
					prompt: entry.prompt,
					choices: entry.choices,
					userChoice: entry.userChoice,
					imageUrl: entry.imageUrl,
				}))
				// currentOptions.value =
				// 	current_story.history[current_story.history.length - 1].choices
				scrollToLastEntry()
			}
		} catch (error) {
			console.error("Error fetching latest story:", error)
		} finally {
			isLoading.value = false
		}
	}

	const startStory = async () => {
		try {
			isLoading.value = true

			const response = await fetchWithAuth("/api/story", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					// Authorization: `Bearer ${tokenStore.token}`,
				},
				body: JSON.stringify({ prompt: storyPrompt.value }),
			})

			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			const data = await response.json()
			if (data.error) {
				Swal.fire({
					icon: "error",
					title: "Error",
					text: data.error || "Failed to start story.",
				})
			} else if (data.story) {
				const { story } = data
				storyStarted.value = true
				isStoryEnded.value = story.isStoryEnded
				storyHistory.value = story.history.map((entry) => ({
					entryId: entry.entryId,
					prompt: entry.prompt,
					choices: entry.choices,
					userChoice: entry.userChoice,
					imageUrl: entry.imageUrl,
				}))
				// currentOptions.value = story.history[story.history.length - 1].choices
				scrollToLastEntry()
			} else {
				// console.log("Error generating story")
				Swal.fire({
					icon: "error",
					title: "Error",
					text: "Error generating story.",
				})
			}

			// }
		} catch (error) {
			console.error("Error starting story:", error)
		} finally {
			isLoading.value = false
		}
	}

	const startNewStory = () => {
		// Show resume button if there was an active story
		showResume.value =
			storyStarted.value ||
			(storyHistory.value && storyHistory.value.length > 0)
		storyPrompt.value = ""
		storyStarted.value = false
		isStoryEnded.value = false
		storyHistory.value = []
		// currentOptions.value = []
		scrollToLastEntry()
	}

	const resumeStory = async () => {
		try {
			isLoading.value = true
			await fetchLatestStory()
			showResume.value = false
		} finally {
			isLoading.value = false
		}
	}

	const scrollToLastEntry = () => {
		nextTick(() => {
			const chatLog = chatLogRef.value
			if (!chatLog) return
			const choiceElement = chatLog.querySelector('[data-latest-choice="true"]')
			const promptElement = chatLog.querySelector('[data-latest-prompt="true"]')

			if (choiceElement) {
				chatLog.scrollTop = choiceElement.offsetTop - chatLog.offsetTop
			} else if (promptElement) {
				chatLog.scrollTop = promptElement.offsetTop - chatLog.offsetTop
			}
		})
	}

	onMounted(() => {
		fetchLatestStory()
	})
</script>

<style scoped>
	.interactive-story {
		--brand: #ff6400;
		--glass: rgba(255, 255, 255, 0.06);
		--border: rgba(255, 255, 255, 0.18);
		--text-primary: #fff;
		--text-muted: #cbd5e1; /* slate-300 */
		/* Subtle tinted glass palettes */
		--ai-bg1: rgba(91, 188, 175, 0.16); /* #5bbcaf */
		--ai-bg2: rgba(91, 188, 175, 0.08);
		--ai-border: rgba(91, 188, 175, 0.35);
		--you-bg1: rgba(255, 100, 0, 0.16); /* brand */
		--you-bg2: rgba(255, 100, 0, 0.08);
		--you-border: rgba(255, 100, 0, 0.35);
		--opt-bg1: rgba(134, 89, 220, 0.14); /* #8659dc */
		--opt-bg2: rgba(134, 89, 220, 0.06);
		--opt-border: rgba(134, 89, 220, 0.38);
		position: relative;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	/* Header */
	.story-header {
		background: radial-gradient(
				800px 120px at 10% 0%,
				rgba(255, 100, 0, 0.25),
				transparent
			),
			radial-gradient(
				600px 100px at 90% 0%,
				rgba(91, 188, 175, 0.22),
				transparent
			);
		border-bottom: 1px solid var(--border);
	}
	.story-header .flex-grow-1 {
		min-width: 0;
	}
	.title {
		font-size: 1.25rem;
		font-weight: 700;
		letter-spacing: 0.2px;
		color: var(--text-primary);
	}
	.subtitle {
		font-size: 0.9rem;
		color: var(--text-muted);
	}
	.icon-pill {
		width: 36px;
		height: 36px;
		min-width: 36px;
		min-height: 36px;
		flex: 0 0 36px; /* prevent flex shrinking on small screens */
		aspect-ratio: 1 / 1; /* keep perfect circle even if height changes */
		border-radius: 999px;
		display: grid;
		place-items: center;
		background: linear-gradient(45deg, var(--brand), #ff914d);
		color: white;
		box-shadow: 0 8px 20px rgba(255, 100, 0, 0.3);
	}
	.badge.beta {
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.08);
		border: 1px solid var(--border);
		color: var(--text-muted);
		padding: 0.35rem 0.6rem;
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}

	/* Chat */
	.chat-log {
		--chat-height: 360px;
		max-height: var(--chat-height);
		overflow-y: auto;
		padding: 1rem 1.25rem 0.5rem;
		gap: 0.75rem;
		display: flex;
		flex-direction: column;
		border-bottom: 1px solid var(--border);
	}
	.message-group + .message-group {
		margin-top: 0.25rem;
	}
	.message {
		display: grid;
		grid-template-columns: 36px 1fr;
		gap: 0.5rem 0.75rem;
		align-items: start;
	}
	.message .avatar {
		width: 36px;
		height: 36px;
		border-radius: 999px;
		display: grid;
		place-items: center;
		font-size: 0.75rem;
		font-weight: 700;
		border: 1px solid var(--border);
		background: rgba(255, 255, 255, 0.04);
		color: var(--text-muted);
	}
	.message .bubble {
		padding: 0.75rem 1rem;
		border-radius: 14px;
		line-height: 1.45;
		color: var(--text-primary);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05),
			0 8px 20px rgba(0, 0, 0, 0.12);
		border: 1px solid var(--border);
		backdrop-filter: blur(10px) saturate(140%);
	}
	.message.ai .bubble {
		background: linear-gradient(180deg, var(--ai-bg1), var(--ai-bg2));
		border-color: var(--ai-border);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05),
			0 8px 20px rgba(91, 188, 175, 0.12);
	}
	.message.ai .avatar {
		background: rgba(91, 188, 175, 0.1);
		border-color: var(--ai-border);
		color: #cdebe6;
	}
	.message.you {
		grid-template-columns: 1fr 36px;
		margin-top: 0.6rem; /* extra separation from choices */
	}
	.message.you .avatar {
		order: 2;
	}
	.message.you .bubble {
		background: linear-gradient(180deg, var(--you-bg1), var(--you-bg2));
		border-color: var(--you-border);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05),
			0 8px 20px rgba(255, 100, 0, 0.14);
	}
	.message.you .avatar {
		background: rgba(255, 100, 0, 0.1);
		border-color: var(--you-border);
		color: #ffe0cc;
	}

	/* Choices */
	.choices {
		margin-left: 44px;
		display: grid;
		grid-template-columns: 1fr;
		gap: 10px;
		margin-top: 0.75rem; /* clear space from AI bubble */
		padding-top: 0.5rem;
		border-top: 1px dashed rgba(255, 255, 255, 0.12);
		margin-bottom: 0.9rem; /* add space before user reply */
	}
	.choice {
		display: grid;
		grid-template-columns: 36px 1fr;
		align-items: center;
		gap: 10px;
		background: linear-gradient(180deg, var(--opt-bg1), var(--opt-bg2));
		border: 1px dashed var(--opt-border);
		border-radius: 12px;
		padding: 0.5rem 0.75rem;
		color: var(--text-primary);
	}
	.choice .num {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background: linear-gradient(180deg, #ff914d, var(--brand));
		color: #fff;
		display: grid;
		place-items: center;
		font-weight: 800;
		font-size: 0.8rem;
		box-shadow: 0 6px 14px rgba(255, 100, 0, 0.25);
	}
	.choice .desc {
		opacity: 0.95;
	}

	/* Awaiting food input indicator */
	.awaiting {
		margin-left: 44px;
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 0.55rem 0.75rem;
		background: linear-gradient(180deg, var(--ai-bg1), var(--ai-bg2));
		border: 1px dashed var(--ai-border);
		border-radius: 12px;
		color: var(--text-primary);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05),
			0 8px 20px rgba(0, 0, 0, 0.1);
		margin-bottom: 0.5rem;
	}
	.awaiting-icon {
		width: 28px;
		height: 28px;
		border-radius: 999px;
		background: linear-gradient(180deg, #ff914d, var(--brand));
		color: #fff;
		display: grid;
		place-items: center;
		box-shadow: 0 6px 14px rgba(255, 100, 0, 0.25);
		flex: 0 0 28px;
	}
	.awaiting-text {
		display: flex;
		flex-direction: column;
		line-height: 1.2;
	}
	.awaiting-text .label {
		font-weight: 600;
	}
	.awaiting-text .sub {
		color: var(--text-muted);
		font-size: 0.85rem;
	}
	.awaiting .awaiting-btn {
		margin-left: auto;
		white-space: nowrap;
	}
	.awaiting .dots {
		margin-left: 4px;
	}
	@media (max-width: 576px) {
		.awaiting {
			flex-direction: column;
			align-items: center;
			text-align: center;
			gap: 8px;
			padding: 0.75rem;
			border-style: solid; /* cleaner look on mobile */
		}
		.awaiting-icon {
			width: 32px;
			height: 32px;
			flex-basis: 32px;
		}
		.awaiting-text {
			align-items: center;
		}
		.awaiting .dots {
			margin: 4px 0 0 0;
			align-self: center;
		}
		.awaiting .awaiting-btn {
			width: 100%;
			margin-left: 0;
		}
	}

	/* Start Screen */
	.start-screen {
		border-top: 1px solid var(--border);
	}
	.hero {
		position: relative;
		padding-top: 1rem;
	}
	.hero .glow {
		position: absolute;
		top: -30px;
		left: 50%;
		transform: translateX(-50%);
		width: 220px;
		height: 120px;
		filter: blur(50px);
		background: radial-gradient(
			closest-side,
			rgba(255, 100, 0, 0.35),
			transparent 60%
		);
		pointer-events: none;
	}
	.hero .emoji {
		font-size: 2rem;
		text-align: center;
	}
	.hero .lead {
		text-align: center;
		color: var(--text-muted);
	}
	.input-wrap {
		position: relative;
		display: flex;
		gap: 0.5rem;
		align-items: stretch;
	}
	.input-field {
		position: relative;
		flex: 1 1 auto;
		min-width: 0;
	}
	.fancy-input {
		flex: 1 1 auto;
		background: rgba(255, 255, 255, 0.06);
		background-color: rgba(255, 255, 255, 0.06);
		border: 1px solid var(--border);
		color: var(--text-primary);
		padding-left: 2.25rem;
		border-radius: 12px;
	}
	/* Keep input glassy on focus (override Bootstrap) */
	.fancy-input:focus {
		background: rgba(255, 255, 255, 0.06) !important;
		background-color: rgba(255, 255, 255, 0.06) !important;
		color: var(--text-primary);
		border-color: var(--border);
		outline: none;
		box-shadow: 0 0 0 0.15rem rgba(255, 100, 0, 0.18);
	}
	.fancy-input::placeholder {
		color: #9aa5b1;
	}
	.input-icon {
		position: absolute;
		left: 12px;
		top: 50%;
		transform: translateY(-50%);
		color: #9aa5b1;
		pointer-events: none;
	}
	.btn-gradient {
		background: linear-gradient(90deg, var(--brand), #ff914d);
		color: white;
		border: none;
		padding: 0.5rem 1rem;
		border-radius: 12px;
		box-shadow: 0 10px 24px rgba(255, 100, 0, 0.28);
		transition: transform 0.12s ease, box-shadow 0.2s ease;
	}
	.btn-gradient:hover {
		transform: translateY(-1px);
		box-shadow: 0 16px 30px rgba(255, 100, 0, 0.35);
	}
	.start-btn {
		white-space: nowrap;
	}

	/* Responsive: stack button under input on small screens */
	@media (max-width: 576px) {
		.input-wrap {
			flex-direction: column;
			align-items: stretch;
		}
		.start-btn {
			width: 100%;
		}
	}

	.suggestions {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		align-items: center;
	}
	.suggestions .hint {
		color: var(--text-muted);
		margin-right: 4px;
	}
	.chip {
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid var(--border);
		border-radius: 999px;
		padding: 0.35rem 0.75rem;
		color: var(--text-primary);
		transition: background 0.15s ease, transform 0.1s ease;
	}
	.chip:hover {
		background: rgba(255, 255, 255, 0.09);
		transform: translateY(-1px);
	}

	/* Footer */
	.footer-actions {
		display: flex;
		justify-content: center;
		padding-top: 1rem;
	}

	/* Ensure story image and bubble occupy the content column (not avatar column) */
	.message.ai .story-image {
		grid-column: 2;
		margin: 0 0 0.5rem 0;
	}
	.message.ai .bubble {
		grid-column: 2;
	}

	.story-image {
		/* no left offset; grid aligns it under content */
		margin: 0.25rem 1.25rem 0.5rem 0;
	}
	.story-image img {
		width: 100%;
		height: auto;
		max-height: clamp(220px, 50vh, 520px);
		object-fit: contain;
		border-radius: 12px;
		border: 1px solid var(--ai-border);
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
	}

	/* System bubble */
	.system-bubble {
		margin: 0.75rem 1.25rem;
		text-align: center;
		color: var(--text-muted);
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid var(--border);
		border-radius: 12px;
		padding: 0.5rem 0.75rem;
	}

	.subtitle .subnote {
		color: var(--text-muted);
		font-size: 0.875rem;
		margin-left: 6px;
	}

	/* Loader */
	.loader-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: grid;
		place-items: center;
		background: rgba(10, 15, 25, 0.62);
		backdrop-filter: blur(8px) saturate(140%);
		z-index: 10;
	}
	.loader-card {
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid var(--border);
		border-radius: 16px;
		color: var(--text-primary);
		box-shadow: 0 14px 40px rgba(0, 0, 0, 0.25);
	}
	.loading-icon {
		font-size: 1.25rem;
		color: var(--brand);
	}
	.dots {
		display: inline-flex;
		gap: 6px;
	}
	.dots span {
		width: 6px;
		height: 6px;
		border-radius: 999px;
		background: var(--brand);
		animation: bounce 1s infinite ease-in-out;
		opacity: 0.9;
	}
	.dots span:nth-child(2) {
		animation-delay: 0.15s;
	}
	.dots span:nth-child(3) {
		animation-delay: 0.3s;
	}
	@keyframes bounce {
		0%,
		80%,
		100% {
			transform: translateY(0);
			opacity: 0.6;
		}
		40% {
			transform: translateY(-6px);
			opacity: 1;
		}
	}

	/* Scrollbar (webkit) */
	.chat-log::-webkit-scrollbar {
		width: 8px;
	}
	.chat-log::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.12);
		border-radius: 8px;
	}
	.chat-log::-webkit-scrollbar-track {
		background: transparent;
	}

	@media (min-width: 992px) {
		/* larger on desktop */
		.chat-log {
			--chat-height: 420px;
		}
	}
</style>
